import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import {
  DRAWER_PARAM,
  DRAWER_ID_PARAM,
  DRAWER_PREFILL_PRODUCT_PARAM,
  DRAWER_PREFILL_LOCATION_PARAM,
  contextToParam,
  paramToContext,
} from "@common/SimpleDrawer/drawerContextParams";

export interface OpenDrawerParams {
  context: DRAWER_CONTEXT;
  /** Entity being edited (omit for create flows). */
  selectedId?: string | null;
  /** Pre-selects a product (create-stock). */
  prefillProductId?: string | null;
  /** Pre-selects a location/parent (create-stock, create-location). */
  prefillLocationId?: string | null;
}

export interface DrawerState {
  isOpen: boolean;
  context: DRAWER_CONTEXT | null;
  selectedId: string | null;
  prefillProductId: string | null;
  prefillLocationId: string | null;
  open: (params: OpenDrawerParams) => void;
  close: () => void;
}

const DRAWER_KEYS = [
  DRAWER_PARAM,
  DRAWER_ID_PARAM,
  DRAWER_PREFILL_PRODUCT_PARAM,
  DRAWER_PREFILL_LOCATION_PARAM,
];

/**
 * The single source of truth for the global side drawer. Reads which form (and
 * its target/prefill) is open from the URL and writes it back on open/close —
 * replacing the old `sideDrawerStore`. Opening pushes a history entry so Back
 * closes the drawer; closing replaces it so Back doesn't reopen it.
 */
export function useDrawer(): DrawerState {
  const [searchParams, setSearchParams] = useSearchParams();
  const context = paramToContext(searchParams.get(DRAWER_PARAM));

  const open = useCallback(
    (params: OpenDrawerParams) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(DRAWER_PARAM, contextToParam(params.context));
        setOrDelete(next, DRAWER_ID_PARAM, params.selectedId);
        setOrDelete(next, DRAWER_PREFILL_PRODUCT_PARAM, params.prefillProductId);
        setOrDelete(
          next,
          DRAWER_PREFILL_LOCATION_PARAM,
          params.prefillLocationId
        );
        return next;
      });
    },
    [setSearchParams]
  );

  const close = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        DRAWER_KEYS.forEach((key) => next.delete(key));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  return useMemo(
    () => ({
      isOpen: context !== null,
      context,
      selectedId: searchParams.get(DRAWER_ID_PARAM),
      prefillProductId: searchParams.get(DRAWER_PREFILL_PRODUCT_PARAM),
      prefillLocationId: searchParams.get(DRAWER_PREFILL_LOCATION_PARAM),
      open,
      close,
    }),
    [context, searchParams, open, close]
  );
}

function setOrDelete(
  params: URLSearchParams,
  key: string,
  value: string | null | undefined
): void {
  if (value) params.set(key, value);
  else params.delete(key);
}
